import { cn } from "@qn/ui";
import * as Dropdown from "@radix-ui/react-dropdown-menu";
import { Brain, Check, ChevronDown, Eye, Sparkles, Wrench, Zap } from "lucide-react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import type { ModelRef, PickerModel } from "../lib/api";
import { useT } from "../lib/i18n";
import { usePrefs } from "../lib/prefs";
import { CostBadge } from "./CostBadge";

export function ModelMenu({
  models,
  value,
  onPick,
  children,
  align = "start",
  onAuto,
}: {
  models: PickerModel[];
  value?: ModelRef | null;
  onPick: (m: PickerModel) => void;
  children: ReactNode;
  align?: "start" | "end";
  /** When given, the menu starts with the "Auto" entry. */
  onAuto?: () => void;
}) {
  const t = useT();
  // Best → worst, in three tiers; inside a tier the more capable first, then the faster.
  const tiers: Array<{ id: "top" | "balanced" | "light"; items: PickerModel[] }> = [
    { id: "top", items: [] },
    { id: "balanced", items: [] },
    { id: "light", items: [] },
  ];
  for (const m of models) {
    const s = m.strength ?? 0;
    tiers[s >= 85 ? 0 : s >= 65 ? 1 : 2]!.items.push(m);
  }
  for (const tr of tiers)
    tr.items.sort(
      (a, b) => (b.strength ?? 0) - (a.strength ?? 0) || (b.speed ?? 3) - (a.speed ?? 3),
    );
  const auto = usePrefs((s) => s.autoModel);
  const lastAuto = usePrefs((s) => s.lastAuto);
  const setPrefs = usePrefs((s) => s.set);

  return (
    <Dropdown.Root>
      <Dropdown.Trigger asChild>{children}</Dropdown.Trigger>
      <Dropdown.Portal>
        <Dropdown.Content
          align={align}
          sideOffset={6}
          className="glass z-50 max-h-[75dvh] w-80 overflow-y-auto rounded-xl border border-[var(--line)] p-1.5 shadow-[var(--shadow)] animate-[fade-in_.12s_ease-out]"
        >
          {models.length === 0 && (
            <Link
              to="/settings/models"
              className="block rounded-lg p-3 text-sm text-[var(--accent-text)] hover:bg-[var(--surface-3)]"
            >
              {t("model.none")}
            </Link>
          )}
          {models.length > 0 && onAuto && (
            <Dropdown.Item
              onSelect={() => {
                setPrefs({ autoModel: true });
                onAuto();
              }}
              className="mb-1 flex cursor-pointer items-start gap-2.5 rounded-lg border border-[var(--line)] px-2.5 py-2.5 text-sm outline-none data-[highlighted]:bg-[var(--surface-3)]"
            >
              <Sparkles className="mt-0.5 size-4 shrink-0 text-[var(--accent-text)]" />
              <span className="min-w-0 flex-1">
                <span className="block font-medium">{t("model.auto")}</span>
                <span className="block text-xs text-[var(--fg-muted)]">{t("model.autoHint")}</span>
                {lastAuto && (
                  <span className="mt-1 block text-xs text-[var(--accent-text)]" dir="auto">
                    {t("model.autoLast", {
                      model: lastAuto.label,
                      why: t(`model.why.${lastAuto.reason}` as "model.why.quick"),
                    })}
                  </span>
                )}
              </span>
              <Check
                className={cn(
                  "mt-0.5 size-4 text-[var(--accent-text)]",
                  auto ? "opacity-100" : "opacity-0",
                )}
              />
            </Dropdown.Item>
          )}
          {tiers
            .filter((tr) => tr.items.length)
            .map((tr) => (
              <Dropdown.Group key={tr.id}>
                <Dropdown.Label className="px-2.5 pb-1 pt-2 text-[11px] font-medium uppercase tracking-wider text-[var(--fg-faint)]">
                  {t(`model.tier.${tr.id}` as "model.tier.top")}
                </Dropdown.Label>
                {tr.items.map((m) => {
                  const selected =
                    !(onAuto && auto) &&
                    value?.providerId === m.providerId &&
                    value.model === m.model;
                  return (
                    <Dropdown.Item
                      key={`${m.providerId}/${m.model}`}
                      onSelect={() => {
                        if (onAuto) setPrefs({ autoModel: false });
                        onPick(m);
                      }}
                      className="flex cursor-pointer items-center gap-2 rounded-lg px-2.5 py-2 text-sm outline-none data-[highlighted]:bg-[var(--surface-3)]"
                    >
                      <span className="min-w-0 flex-1">
                        <span className="block truncate">{m.label}</span>
                        <span className="block truncate text-[11px] text-[var(--fg-faint)]">
                          {m.providerName}
                        </span>
                      </span>
                      {m.cost !== "free" && <CostBadge cost={m.cost} compact />}
                      <span
                        className="flex items-center text-[var(--fg-faint)]"
                        title={t("model.speed")}
                      >
                        {Array.from({ length: m.speed ?? 3 }, (_, i) => (
                          <Zap key={i} className="-me-1 size-3 fill-current" />
                        ))}
                      </span>
                      <span className="flex items-center gap-1 text-[var(--fg-faint)]">
                        {m.capabilities.vision && (
                          <Eye className="size-3.5" aria-label={t("model.vision")} />
                        )}
                        {m.capabilities.thinking && (
                          <Brain className="size-3.5" aria-label={t("model.thinking")} />
                        )}
                        {m.capabilities.tools && (
                          <Wrench className="size-3.5" aria-label={t("model.tools")} />
                        )}
                      </span>
                      <Check
                        className={cn(
                          "size-4 text-[var(--accent-text)]",
                          selected ? "opacity-100" : "opacity-0",
                        )}
                      />
                    </Dropdown.Item>
                  );
                })}
              </Dropdown.Group>
            ))}
        </Dropdown.Content>
      </Dropdown.Portal>
    </Dropdown.Root>
  );
}

export function ModelPicker({
  models,
  value,
  onPick,
}: {
  models: PickerModel[];
  value?: PickerModel | null;
  onPick: (m: PickerModel) => void;
}) {
  const t = useT();
  const auto = usePrefs((s) => s.autoModel);
  return (
    <ModelMenu models={models} value={value} onPick={onPick} onAuto={() => undefined}>
      <button
        type="button"
        className="flex max-w-[60vw] items-center gap-2 rounded-xl border border-[var(--line)] bg-[var(--surface)] px-3 py-1.5 text-sm font-medium transition hover:border-[var(--line-strong)] hover:bg-[var(--surface-2)]"
        aria-label={t("model.pick")}
      >
        {auto ? (
          <Sparkles className="size-4 shrink-0 text-[var(--accent-text)]" />
        ) : (
          <span className="size-2 shrink-0 rounded-full bg-[var(--accent)]" />
        )}
        <span className="truncate">
          {auto ? t("model.auto") : (value?.label ?? t("model.select"))}
        </span>
        <ChevronDown className="size-4 shrink-0 text-[var(--fg-faint)]" />
      </button>
    </ModelMenu>
  );
}
